
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { useOptimizedCardPresenceContext } from "@/contexts/OptimizedCardPresenceContext";
import { useAuth } from "@/hooks/useAuth";
import { useEffect, useMemo } from "react";

interface CardPresenceAvatarsProps {
  cardId: string;
  currentStatus?: "viewing" | "editing";
  small?: boolean;
  debug?: boolean;
}

export function CardPresenceAvatars({ 
  cardId,
  currentStatus = "viewing",
  small,
  debug = false 
}: CardPresenceAvatarsProps) {
  // Use the optimized presence context (optional)
  let presence: any = {};
  let isConnected = false;
  let setCardStatus: ((cardId: string, status: "viewing" | "editing") => void) | undefined;
  let clearCardPresence: ((cardId: string) => void) | undefined;
  let trackCardVisibility: ((cardId: string, isVisible: boolean) => void) | undefined;
  
  try {
    const context = useOptimizedCardPresenceContext();
    presence = context.presence;
    isConnected = context.isConnected;
    setCardStatus = context.setCardStatus;
    clearCardPresence = context.clearCardPresence;
    trackCardVisibility = context.trackCardVisibility;
  } catch {
    // Context not available - presence tracking not needed in this case
  }
  
  const { user } = useAuth();

  // Set the current status when component mounts or status changes
  useEffect(() => {
    if (cardId && user?.id && setCardStatus && trackCardVisibility) {
      setCardStatus(cardId, currentStatus);
      trackCardVisibility(cardId, true);
    }
    
    // Cleanup when component unmounts
    return () => {
      if (cardId && clearCardPresence && trackCardVisibility) {
        clearCardPresence(cardId);
        trackCardVisibility(cardId, false);
      }
    };
  }, [cardId, currentStatus, user?.id, setCardStatus, clearCardPresence, trackCardVisibility]);

  // Memoize filtered presence data for performance
  const { others, editingUsers } = useMemo(() => {
    const cardPresence = presence[cardId] || [];
    const filteredOthers = cardPresence.filter((p) => p.user_id !== user?.id);
    const filteredEditing = filteredOthers.filter(p => p.status === "editing");
    
    return {
      others: filteredOthers,
      editingUsers: filteredEditing
    };
  }, [presence, cardId, user?.id]);
  
  // Debug logging
  if (debug) {
    console.log('🎭 CardPresenceAvatars render:', {
      cardId,
      totalPresence: (presence[cardId] || []).length,
      others: others.length,
      userId: user?.id,
      isConnected,
      presenceData: presence[cardId],
      othersData: others
    });
  }
  
  if (others.length === 0) {
    if (debug) {
      console.log('🎭 No others present, not rendering avatars');
    }
    return null;
  }

  // show up to 3 others
  const visible = others.slice(0, 3);

  if (debug) {
    console.log('🎭 Rendering avatars:', {
      visible: visible.length,
      editing: editingUsers.length,
      visibleUsers: visible.map(u => ({ id: u.user_id, name: u.user_name, status: u.status }))
    });
  }

  return (
    <div className="flex items-center gap-1">
      <div className="flex -space-x-2">
        {visible.map((user) => (
          <Avatar
            key={user.user_id}
            className={`${small ? "h-6 w-6" : "h-8 w-8"} border-2 border-white shadow-sm transition-all duration-200`}
            title={`${user.user_name ?? "User"} - ${user.status}`}
            style={{
              outline: user.status === "editing" ? "2px solid #22c55e" : "1px solid #e5e7eb",
              outlineOffset: "-2px",
              zIndex: 2,
              transform: user.status === "editing" ? "scale(1.1)" : "scale(1)",
            }}
          >
            <AvatarFallback 
              className={`font-bold text-xs ${
                user.status === "editing" 
                  ? "bg-green-500 text-white" 
                  : "bg-primary text-primary-foreground dark:bg-slate-600 dark:text-slate-100"
              }`}
            >
              {(user.user_name ?? "?").slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
        ))}
        {others.length > 3 && (
          <div className={`${small ? "h-6 w-6" : "h-8 w-8"} rounded-full bg-gray-200 dark:bg-gray-700 border-2 border-white flex items-center justify-center`}>
            <span className="text-xs text-gray-600 dark:text-gray-400 font-bold">
              +{others.length - 3}
            </span>
          </div>
        )}
      </div>
      
      {/* Show editing indicator badge if someone is editing */}
      {editingUsers.length > 0 && (
        <Badge 
          variant="secondary" 
          className="text-xs bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 animate-pulse"
        >
          {editingUsers.length === 1 ? "Editing" : `${editingUsers.length} editing`}
        </Badge>
      )}
      
      {/* Connection status indicator for debug */}
      {debug && (
        <Badge variant={isConnected ? "default" : "destructive"} className="text-xs">
          {isConnected ? "Connected" : "Disconnected"}
        </Badge>
      )}
    </div>
  );
}
